import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AdminCertificate, AdminCertificateHealth, AdminDomain } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Filter } from "@/ui/Filter";
import { Chip, type ChipTone } from "@/ui/Chip";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, TableSkeleton } from "@/ui/States";
import { useFilters } from "@/features/admin/useFilters";
import { absoluteTime, cx, shortTime } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/certificates")({
  component: CertificatesScreen,
});

/**
 * The certificates and the domains they are for.
 *
 * # WHY ONE SCREEN AND NOT TWO
 *
 * Because the question that matters spans both. A custom domain that is
 * verified and has no certificate is a hostname Drumlin has promised to answer
 * on and cannot; neither table says so on its own, and reading them on separate
 * screens means nobody ever puts the two together.
 *
 * A certificate running out is a customer outage with thirty days of warning.
 * Nothing was using the warning.
 */
function CertificatesScreen() {
  const filters = useFilters();
  const failingOnly = filters.on("attention");
  const setFailingOnly = (value: boolean) => filters.toggle("attention", value);

  const certificates = useQuery({
    queryKey: ["platform-certificates"],
    queryFn: ({ signal }) =>
      api.get<{ certificates: AdminCertificate[] | null; health: AdminCertificateHealth }>(
        "/admin/certificates",
        { limit: 300 },
        signal,
      ),
    refetchInterval: 60000,
  });

  const domains = useQuery({
    queryKey: ["platform-domains"],
    queryFn: ({ signal }) =>
      api.get<{ domains: AdminDomain[] | null }>("/admin/domains", { limit: 300 }, signal),
    refetchInterval: 60000,
  });

  const health = certificates.data?.health;
  const allCerts = certificates.data?.certificates ?? [];
  const certs = failingOnly
    ? allCerts.filter((c) => c.status !== "issued" || expiresWithin(c.not_after, 30))
    : allCerts;
  const domainList = domains.data?.domains ?? [];
  // The rows that are a promise nobody can keep: verified, so Drumlin has told
  // the customer to point DNS at us, and no certificate to answer with.
  // 'active' is what a verified domain is called. The column's own constraint
  // allows pending, verifying, active and failed — a filter written against
  // "verified" would match nothing and quietly report that all is well.
  const uncovered = domainList.filter(
    (d) => d.status === "active" && d.certificate_status !== "issued",
  );

  return (
    <div className="flex flex-col gap-4">
      {health && (health.expired > 0 || health.failing > 0 || uncovered.length > 0) && (
        <div className="rounded-xl border border-[var(--danger)] bg-[color-mix(in_oklab,_var(--danger)_12%,_transparent)] px-4 py-3">
          <p className="text-[14px] font-medium text-[var(--danger)]">
            {health.expired > 0 && `${health.expired} expired`}
            {health.expired > 0 && (health.failing > 0 || uncovered.length > 0) && " · "}
            {health.failing > 0 && `${health.failing} failing to issue`}
            {health.failing > 0 && uncovered.length > 0 && " · "}
            {uncovered.length > 0 && `${uncovered.length} verified without a certificate`}
          </p>
          <p className="mt-0.5 text-[13px] text-[var(--text-secondary)]">
            Each of these is a hostname a customer was told to point at Drumlin that will not
            answer over TLS.
          </p>
        </div>
      )}

      {health && (
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Count label="Certificates" value={health.total} />
          <Count label="Issued" value={health.issued} />
          <Count label="Failing" value={health.failing} tone={health.failing > 0 ? "down" : undefined} />
          <Count
            label="Expire in 7 days"
            value={health.expiring_in_7_days}
            tone={health.expiring_in_7_days > 0 ? "warn" : undefined}
          />
          <Count label="Expire in 30 days" value={health.expiring_in_30_days} />
          <Count label="Expired" value={health.expired} tone={health.expired > 0 ? "down" : undefined} />
        </div>
      )}

      <Panel
        toolbar={
          <Filter
            label="Needs attention"
            tone="down"
            active={failingOnly}
            onClick={() => setFailingOnly(!failingOnly)}
          />
        }
        padded={false}
      >
        {certificates.isLoading ? (
          <div className="px-5 py-4">
            <TableSkeleton rows={6} columns={5} />
          </div>
        ) : certificates.isError ? (
          <div className="px-5 py-4">
            <ErrorState error={certificates.error} onRetry={() => void certificates.refetch()} />
          </div>
        ) : certs.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState
              title={failingOnly ? "Nothing needs attention" : "No certificates"}
              description={
                failingOnly
                  ? "Every certificate is issued and none expires within thirty days."
                  : "No custom domain has asked for one yet."
              }
            />
          </div>
        ) : (
          <div className="px-5 py-3">
            <Table dense fixed>
              <thead>
                <Tr>
                  <Th width="w-[30%]">Hostname</Th>
                  <Th width="w-[20%]">Tenant</Th>
                  <Th width="w-[14%]">Status</Th>
                  <Th width="w-[14%]" align="right">
                    Expires
                  </Th>
                  <Th width="w-[14%]" align="right">
                    Renews
                  </Th>
                  <Th width="w-[8%]" numeric>
                    Tries
                  </Th>
                </Tr>
              </thead>
              <tbody>
                {certs.map((c) => (
                  <Tr key={c.id} className={rowTone(c)}>
                    <Td clip title={c.status_reason || c.hostname}>
                      {c.hostname}
                    </Td>
                    <Td clip title={c.organisation_name}>
                      <Link
                        to="/admin/$orgId"
                        params={{ orgId: c.organisation_id }}
                        className="hover:text-[var(--primary)]"
                      >
                        {c.organisation_name}
                      </Link>
                    </Td>
                    <Td>
                      <Chip tone={certificateTone(c)}>{c.status}</Chip>
                    </Td>
                    <Td align="right" title={c.not_after ? absoluteTime(c.not_after) : undefined}>
                      {c.not_after ? shortTime(c.not_after) : "never issued"}
                    </Td>
                    <Td align="right" title={c.renew_after ? absoluteTime(c.renew_after) : undefined}>
                      {c.renew_after ? shortTime(c.renew_after) : "—"}
                    </Td>
                    <Td numeric>{c.attempts || "—"}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>

      <Panel
        title="Custom domains"
        description="Unverified first, then by how long since anybody checked. A verified domain with no certificate is a hostname we told a customer to point at us and cannot answer on."
        padded={false}
      >
        {domains.isLoading ? (
          <div className="px-5 py-4">
            <TableSkeleton rows={4} columns={5} />
          </div>
        ) : domains.isError ? (
          <div className="px-5 py-4">
            <ErrorState error={domains.error} onRetry={() => void domains.refetch()} />
          </div>
        ) : domainList.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState title="No custom domains" description="Nobody has added one." />
          </div>
        ) : (
          <div className="px-5 py-3">
            <Table dense fixed>
              <thead>
                <Tr>
                  <Th width="w-[28%]">Hostname</Th>
                  <Th width="w-[18%]">Tenant</Th>
                  <Th width="w-[16%]">Service</Th>
                  <Th width="w-[12%]">Domain</Th>
                  <Th width="w-[13%]">Certificate</Th>
                  <Th width="w-[13%]" align="right">
                    Checked
                  </Th>
                </Tr>
              </thead>
              <tbody>
                {domainList.map((d) => {
                  const bare = d.status === "active" && d.certificate_status !== "issued";
                  return (
                    <Tr key={d.id} className={bare ? "bg-[color-mix(in_oklab,_var(--danger)_12%,_transparent)]" : undefined}>
                      <Td clip title={d.status_reason || d.hostname}>
                        {d.hostname}
                      </Td>
                      <Td clip title={d.organisation_name}>
                        <Link
                          to="/admin/$orgId"
                          params={{ orgId: d.organisation_id }}
                          className="hover:text-[var(--primary)]"
                        >
                          {d.organisation_name}
                        </Link>
                      </Td>
                      <Td clip title={d.service_name || undefined}>
                        {d.service_name || "—"}
                      </Td>
                      <Td>
                        <Chip tone={d.status === "active" ? "up" : "warn"}>
                          {d.status}
                        </Chip>
                      </Td>
                      <Td>
                        {d.certificate_status ? (
                          <Chip tone={d.certificate_status === "issued" ? "up" : "down"}>
                            {d.certificate_status}
                          </Chip>
                        ) : (
                          <Chip tone={bare ? "down" : undefined}>none</Chip>
                        )}
                      </Td>
                      <Td
                        align="right"
                        title={d.last_checked_at ? absoluteTime(d.last_checked_at) : undefined}
                      >
                        {d.last_checked_at ? shortTime(d.last_checked_at) : "never"}
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>
    </div>
  );
}

/** Whether a moment is inside the next n days. */
function expiresWithin(iso: string | undefined, days: number): boolean {
  if (!iso) return true;
  const at = new Date(iso).getTime();
  if (!Number.isFinite(at)) return true;
  return at - Date.now() < days * 86400000;
}

function certificateTone(c: AdminCertificate): ChipTone | undefined {
  if (c.status !== "issued") return c.status === "pending" ? "warn" : "down";
  if (!c.not_after) return "down";
  if (new Date(c.not_after).getTime() <= Date.now()) return "down";
  return expiresWithin(c.not_after, 7) ? "warn" : "up";
}

function rowTone(c: AdminCertificate): string | undefined {
  const tone = certificateTone(c);
  if (tone === "down") return "bg-[color-mix(in_oklab,_var(--danger)_12%,_transparent)]";
  if (tone === "warn") return "bg-[color-mix(in_oklab,_var(--warning)_12%,_transparent)]";
  return undefined;
}

function Count({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: ChipTone | undefined;
}) {
  return (
    <div className="card rounded-xl px-4 py-3">
      <p className="text-[11px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
        {label}
      </p>
      <p
        className={cx(
          "mt-1 text-xl font-semibold tabular-nums tracking-tight",
          tone === "down"
            ? "text-[var(--danger)]"
            : tone === "warn"
              ? "text-[var(--warning)]"
              : "text-[var(--text-primary)]",
        )}
      >
        {value.toLocaleString()}
      </p>
    </div>
  );
}
